import {
    BENEFIT_SECTION_TYPES,
    BENEFIT_TIERS,
    BENEFITS_DESTINATION,
    CREDIT_CARD_DESTINATION,
    FAQS_DESTINATION
} from 'components/Content/BeautyInsider/BeautyInsiderBenefits/constants';
import localeUtils from 'utils/LanguageLocale';
import BeautyInsiderBenefitsEN from 'components/Content/BeautyInsider/BeautyInsiderBenefits/locales/BeautyInsiderBenefits_en_US';
import BeautyInsiderBenefitsFR from 'components/Content/BeautyInsider/BeautyInsiderBenefits/locales/BeautyInsiderBenefits_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(BeautyInsiderBenefitsEN, BeautyInsiderBenefitsFR);

const VIEW_ALL_BENEFITS_LINK = {
    labelKey: 'viewAllBenefits',
    href: BENEFITS_DESTINATION
};

const FAQS_LINK = {
    labelKey: 'faqs',
    href: FAQS_DESTINATION
};

const FEATURED_BENEFITS_BY_TIER = {
    [BENEFIT_TIERS.NON_BI]: {
        titleKey: 'beautyInsiderBenefitsTitle',
        benefits: [
            { id: 'freeShipping', icon: '🚚', titleKey: 'featuredFreeShippingEveryTime' },
            { id: 'birthdayGift', icon: '🎂', titleKey: 'featuredBirthdayGiftSet' },
            { id: 'savingsExperiences', icon: '👛', titleKey: 'featuredPointsSavingsExperiences' }
        ]
    },
    [BENEFIT_TIERS.BI]: {
        titleKey: 'featuredBenefitsAsInsiderTitle',
        benefits: [
            { id: 'freeShipping', icon: '🚚', titleKey: 'featuredFreeShippingEveryTime' },
            { id: 'birthdayGift', icon: '🎂', titleKey: 'featuredBirthdayGiftSet' },
            { id: 'pointsSavings', icon: '👛', titleKey: 'featuredPointsSavingsExperiences' }
        ]
    },
    [BENEFIT_TIERS.VIB]: {
        titleKey: 'featuredBenefitsAsVibTitle',
        benefits: [
            { id: 'seasonalSavings', icon: '🛍️', titleKey: 'featuredVibDiscount' },
            { id: 'points', icon: '💎', titleKey: 'featuredVibPoints' },
            { id: 'exclusiveGift', icon: '🤩', titleKey: 'featuredExclusiveGift' },
            { id: 'freeShipping', icon: '🚚', titleKey: 'featuredFreeShippingEveryTime' },
            { id: 'birthdayGift', icon: '🎂', titleKey: 'featuredBirthdayGiftSet' },
            { id: 'pointsSavings', icon: '👛', titleKey: 'featuredPointsSavingsExperiences' }
        ]
    },
    [BENEFIT_TIERS.ROUGE]: {
        titleKey: 'featuredBenefitsAsRougeTitle',
        benefits: [
            { id: 'seasonalSavings', icon: '🛍️', titleKey: 'featuredRougeDiscount' },
            { id: 'points', icon: '💎', titleKey: 'featuredRougePoints' },
            { id: 'firstAccess', icon: '✨', titleKey: 'featuredFirstAccess' },
            { id: 'exclusiveGift', icon: '🤩', titleKey: 'featuredExclusiveGift' },
            { id: 'freeShipping', icon: '🚚', titleKey: 'featuredFreeShippingEveryTime' },
            { id: 'birthdayGift', icon: '🎂', titleKey: 'featuredBirthdayGiftSet' },
            { id: 'pointsSavings', icon: '👛', titleKey: 'featuredPointsSavingsExperiences' }
        ]
    }
};

const NEXT_TIER_BY_TIER = {
    [BENEFIT_TIERS.BI]: {
        tier: BENEFIT_TIERS.VIB,
        label: 'VIB',
        titleKey: 'whenYouReachVib',
        benefits: [
            { id: 'vibDiscount', icon: '🔒', titleKey: 'featuredVibDiscount' },
            { id: 'vibPoints', icon: '🔒', titleKey: 'featuredVibPoints' },
            { id: 'exclusiveGift', icon: '🔒', titleKey: 'featuredExclusiveGift' }
        ],
        action: FAQS_LINK
    },
    [BENEFIT_TIERS.VIB]: {
        tier: BENEFIT_TIERS.ROUGE,
        label: 'Rouge',
        titleKey: 'whenYouReachRouge',
        benefits: [
            { id: 'rougeDiscount', icon: '🔒', titleKey: 'featuredRougeDiscount' },
            { id: 'rougePoints', icon: '🔒', titleKey: 'featuredRougePoints' },
            { id: 'firstAccess', icon: '🔒', titleKey: 'featuredFirstAccess' }
        ],
        action: FAQS_LINK
    }
};

const CREDIT_CARD_HOLDER_SECTION = {
    titleKey: 'creditCardHolderTitle',
    benefits: [
        { id: 'creditRewards', icon: '⚡', titleKey: 'creditCardHolderEarnRewards' },
        { id: 'pointsEarned', icon: '🏆', titleKey: 'creditCardHolderEarnPoints' },
        { id: 'firstPurchase', icon: '🛍️', titleKey: 'creditCardHolderFirstPurchase' }
    ],
    action: {
        labelKey: 'creditCardHolderCta',
        href: CREDIT_CARD_DESTINATION
    }
};

const getBenefitLink = link => ({
    label: getText(link.labelKey),
    href: link.href
});

const getBenefit = benefit => ({
    id: benefit.id,
    ...(benefit.icon && { icon: benefit.icon }),
    title: getText(benefit.titleKey),
    ...(benefit.descriptionKey && { description: getText(benefit.descriptionKey) })
});

const getBenefitSection = section => ({
    title: getText(section.titleKey),
    ...(section.descriptionKey && { description: getText(section.descriptionKey) }),
    benefits: section.benefits.map(getBenefit)
});

/**
 * Builds the featured Benefits section for a normalized BI tier.
 *
 * @param {string} tier Benefits tier key.
 * @returns {Object} Featured Benefits section state.
 */
const buildFeaturedBenefitsSection = tier => ({
    id: BENEFIT_SECTION_TYPES.FEATURED_BENEFITS,
    type: BENEFIT_SECTION_TYPES.FEATURED_BENEFITS,
    ...getBenefitSection(FEATURED_BENEFITS_BY_TIER[tier]),
    links:
        tier === BENEFIT_TIERS.BI || tier === BENEFIT_TIERS.VIB
            ? [getBenefitLink(VIEW_ALL_BENEFITS_LINK)]
            : [getBenefitLink(VIEW_ALL_BENEFITS_LINK), getBenefitLink(FAQS_LINK)]
});

/**
 * Builds the next-tier Benefits section when the current tier can advance.
 *
 * @param {string} tier Benefits tier key.
 * @returns {Object|null} Next-tier section state, or null for terminal tiers.
 */
const buildNextTierSection = tier => {
    const nextTier = NEXT_TIER_BY_TIER[tier];

    if (!nextTier) {
        return null;
    }

    return {
        id: BENEFIT_SECTION_TYPES.NEXT_TIER,
        type: BENEFIT_SECTION_TYPES.NEXT_TIER,
        tier: nextTier.tier,
        label: nextTier.label,
        ...getBenefitSection(nextTier),
        action: getBenefitLink(nextTier.action || VIEW_ALL_BENEFITS_LINK)
    };
};

/**
 * Builds the U.S. cardholder-only Credit Card Benefits section.
 *
 * @returns {Object} Credit Card section state.
 */
const buildCreditCardSection = () => ({
    id: BENEFIT_SECTION_TYPES.CREDIT_CARD,
    type: BENEFIT_SECTION_TYPES.CREDIT_CARD,
    ...getBenefitSection(CREDIT_CARD_HOLDER_SECTION),
    action: getBenefitLink(CREDIT_CARD_HOLDER_SECTION.action)
});

export {
    VIEW_ALL_BENEFITS_LINK,
    FAQS_LINK,
    FEATURED_BENEFITS_BY_TIER,
    NEXT_TIER_BY_TIER,
    buildFeaturedBenefitsSection,
    buildNextTierSection,
    buildCreditCardSection
};
